import { Box, Button } from '@mui/material'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useCallback, useContext, useEffect, useMemo } from 'react'

// others
import dayjs from 'dayjs'
import { CardBox } from '../theme/styleComponents'
import { flexCenter } from '../theme/styleConstants'
import { ActionType } from '../context/AppContextReducer'
import { AppContext, AuthContext } from '../context'
import { getEntry, hasAdminAccess, resetEntryType } from '../utils'
import {
  Entry,
  EntryOrderBy,
  EntryOrderDirection,
  EntryStatus,
  EntryType,
  useFetchAllEntriesLazyQuery
} from '../../generated'
import {
  ADD_ENTRY,
  ENTRIES_PAGE_LIMIT,
  ENTRIES_SORT_VALUES,
  FILTER_DATE_FORMAT,
  GRAPHQL_QUERY_POLICY,
  QUERY_PARAMS
} from '../constants'
import { EntryActionType } from '../components/entries/EntryReducer'

// components
import { ContentHead } from '../components/common/ContentHead'
import { EmptyData } from '../components/common/EmptyData'
import EntryFilters from '../components/entries/EntryFilters'
import EntryListing from '../components/entries/EntryListing'
import ManualEntry from '../components/entries/ManualEntry'
import { EntryActions } from '../interfaceTypes/common'
import { EntryContext } from '../context/EntryContext'

const EntryContainer = () => {
  const { currentUser } = useContext(AuthContext)
  const { dispatch: entryDispatch, ...entryState } = useContext(EntryContext)
  const { supervisingUsers } = currentUser || {}
  const supervisingUsersIds = useMemo(() => supervisingUsers?.map(user => user?.id || ''), [supervisingUsers])

  const { dispatch: reducerDispatch, currentPage, isOpen, isNonClientEntry } = useContext(AppContext)

  const [searchParams] = useSearchParams()

  const getParam = (key: QUERY_PARAMS) => searchParams.get(key)

  const status = getParam(QUERY_PARAMS.STATUS) || QUERY_PARAMS.ALL
  const caseId = getParam(QUERY_PARAMS.MATTER) || QUERY_PARAMS.ALL
  const categoryId = getParam(QUERY_PARAMS.CATEGORY) || QUERY_PARAMS.ALL
  const userId = getParam(QUERY_PARAMS.USER) || QUERY_PARAMS.ALL

  const fromParam = getParam(QUERY_PARAMS.FROM)
  const toParam = getParam(QUERY_PARAMS.TO)
  const validEntriesSortValues: string[] = [
    ENTRIES_SORT_VALUES.MOST_RECENT,
    ENTRIES_SORT_VALUES.ACTIVITY_NEWEST,
    ENTRIES_SORT_VALUES.ACTIVITY_OLDEST
  ]
  const entriesSortParam = getParam(QUERY_PARAMS.ENTRIES_SORT)
  const entriesSort =
    entriesSortParam && validEntriesSortValues.includes(entriesSortParam)
      ? entriesSortParam
      : ENTRIES_SORT_VALUES.MOST_RECENT
  const param = useParams()

  const { type } = param
  const normalizedType = type ? type.replace('-', '_').toLocaleUpperCase() : ''

  const fromDate = useMemo(
    () =>
      fromParam
        ? dayjs(fromParam!).format(FILTER_DATE_FORMAT)
        : dayjs(new Date()).subtract(1, 'month').format(FILTER_DATE_FORMAT),
    [fromParam]
  )
  const toDate = useMemo(
    () => (toParam ? dayjs(toParam!).format(FILTER_DATE_FORMAT) : dayjs().add(1, 'day').format(FILTER_DATE_FORMAT)),
    [toParam]
  )

  const getTimeEntryStatus = useCallback(() => {
    switch (status?.toLocaleUpperCase()) {
      case EntryStatus.Billed:
        return [EntryStatus.Billed]
      case EntryStatus.Draft:
        return [EntryStatus.Draft]
      case EntryStatus.Unbilled:
        return [EntryStatus.Unbilled]
      default:
        return [EntryStatus.Billed, EntryStatus.Draft, EntryStatus.Unbilled]
    }
  }, [status])

  const { allEntries } = entryState ?? {}

  const [fetchAllTimeEntries, { loading: allLoading, refetch: allRefetch }] = useFetchAllEntriesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    fetchPolicy: 'cache-first',

    variables: undefined,
    onError(error) {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      entryDispatch({ type: EntryActionType.SET_ALL_ENTRIES, allEntries: [] })
    },

    onCompleted(data) {
      const {
        fetchAllEntries: { entries, pagination }
      } = data || {}
      entryDispatch({ type: EntryActionType.SET_ALL_ENTRIES, allEntries: [] })

      // Then update with new data
      if (entries?.length) {
        entryDispatch({ type: EntryActionType.SET_ALL_ENTRIES, allEntries: entries as Entry[] })
      }
      if (pagination) {
        const { totalPages, totalCount } = pagination

        reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages || 1 })
        reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount || 1 })
      }
    }
  })

  const resetAndSetState = useCallback(
    (actionType: EntryActions) => {
      resetEntryType(reducerDispatch)
      reducerDispatch({ type: actionType, value: true })
    },
    [reducerDispatch]
  )

  const setEntryType = useCallback(() => {
    switch (normalizedType) {
      case EntryType.Time:
        resetAndSetState(ActionType.SET_TIME_ENTRY)
        break
      case EntryType.Expense:
        resetAndSetState(ActionType.SET_IS_EXPENSE_ENTRY)
        break
      case EntryType.Cost:
        resetAndSetState(ActionType.SET_COST_ENTRY)
        break
      case EntryType.Legal:
        resetAndSetState(ActionType.SET_LEGAL_FEE)
        break
      case EntryType.NonClient:
        resetAndSetState(ActionType.SET_NON_CLIENT_ENTRY)
        break
      default:
        break
    }
  }, [normalizedType, resetAndSetState])

  const entrySortOrderBy =
    entriesSort === ENTRIES_SORT_VALUES.ACTIVITY_NEWEST || entriesSort === ENTRIES_SORT_VALUES.ACTIVITY_OLDEST
      ? EntryOrderBy.Date
      : EntryOrderBy.CreatedAt
  const entrySortDirection =
    entriesSort === ENTRIES_SORT_VALUES.ACTIVITY_OLDEST ? EntryOrderDirection.Asc : EntryOrderDirection.Desc

  const getEntriesVariables = useCallback(
    () => ({
      fetchTimeEntriesInput: {
        paginationOptions: {
          limit: ENTRIES_PAGE_LIMIT,
          page: currentPage
        },
        filters: {
          status: getTimeEntryStatus(),
          caseId: caseId !== QUERY_PARAMS.ALL ? [caseId] : undefined,
          categoryId: categoryId !== QUERY_PARAMS.ALL ? [categoryId] : undefined,
          userId: userId !== QUERY_PARAMS.ALL ? userId : undefined,
          entryType: getEntry(normalizedType).type,
          fromDate,
          toDate
        },
        orderBy: entrySortOrderBy,
        order: entrySortDirection
      }
    }),
    [
      currentPage,
      getTimeEntryStatus,
      caseId,
      categoryId,
      userId,
      normalizedType,
      fromDate,
      toDate,
      entrySortOrderBy,
      entrySortDirection
    ]
  )

  const handleRefresh = useCallback(() => {
    fetchAllTimeEntries({
      variables: getEntriesVariables(),
      fetchPolicy: 'network-only'
    })
  }, [fetchAllTimeEntries, getEntriesVariables])

  useEffect(() => {
    fetchAllTimeEntries({
      variables: getEntriesVariables()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser?.id,
    currentPage,
    getTimeEntryStatus,
    caseId,
    categoryId,
    userId,
    supervisingUsersIds,
    normalizedType,
    fromDate,
    toDate,
    entriesSort,
    entrySortOrderBy,
    entrySortDirection,
    currentUser?.id,
    fetchAllTimeEntries,
    getEntriesVariables
  ])

  useEffect(() => {
    setEntryType()
    reducerDispatch({
      type: ActionType.SET_ENTRY_TYPE,
      entryType: Object.values(EntryType).find((entry: string) => entry === normalizedType) || EntryType.Time
    })
  }, [reducerDispatch, setEntryType, normalizedType])

  useEffect(() => {
    reducerDispatch({
      type: ActionType.SET_ENTRY_TYPE,
      entryType: Object.values(EntryType).find((entry: string) => entry === normalizedType) || EntryType.Time
    })
    return () => reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedType])

  const isEmpty = !allLoading && allEntries?.length === 0

  const handleClick = () => reducerDispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })

  const showCustomButtonForEntries = () => {
    if (!hasAdminAccess(currentUser)) return null

    switch (normalizedType) {
      case EntryType.Expense:
        return (
          <Button color="primary" variant="outlined" component={Link} to={'categories'} aria-label={'Manage Category'}>
            Manage Category
          </Button>
        )
      case EntryType.Legal:
        return (
          <Button color="primary" variant="outlined" component={Link} to={'fee-types'} aria-label={'Manage Fee Types'}>
            Manage Fee Types
          </Button>
        )
      default:
        return undefined
    }
  }
  return (
    <>
      <ManualEntry
        isOpen={isOpen}
        handleClose={handleClick}
        refetch={allRefetch}
        title={getEntry(normalizedType).title}
      />

      <CardBox marginTop={3}>
        <ContentHead
          title={getEntry(normalizedType).label}
          buttonText={ADD_ENTRY}
          customButton={showCustomButtonForEntries}
          customOnClick={() => {
            entryDispatch({ type: EntryActionType.SET_CURRENT_ENTRY, currentEntry: null })
          }}
        />

        <EntryFilters
          isNonClientEntry={isNonClientEntry}
          onRefresh={handleRefresh}
          loading={allLoading}
        />

        {isEmpty ? (
          <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
            <EmptyData title={'No Entries!'} />
          </Box>
        ) : (
          <EntryListing state={entryState} entryDispatch={entryDispatch} loading={allLoading} refetch={allRefetch} />
        )}
      </CardBox>
    </>
  )
}

export default EntryContainer
